<template>
  <b-card class="">
    <div class="header-current d-flex justify-content-between align-items-center flex-wrap">
      <div class="d-flex align-items-center flex-wrap">
        <div class="d-flex align-items-center mr-1">
          {{ $t('membersWhitelabel') }}
          <b-badge variant="primary" class="ml-1">{{ total_rows }}</b-badge>
        </div>

        <div class="search-bar-container" v-if="loaded">
          <b-form-input
            v-model="sarch_query"
            :placeholder="$t('searchMember')"
            @input="handleSearch"
            class="search-input"
          >
            <template #prepend>
              <feather-icon icon="SearchIcon" size="18" />
            </template>
          </b-form-input>
        </div>
      </div>
      <b-button
        variant="primary"
        size="sm"
        @click="openAddMemberSidebar"
      >
        <feather-icon icon="PlusIcon" size="16" class="mr-50" />
        {{ $t('addMember') }}
      </b-button>
    </div>
    <div class="main-container-current-membership p-0">
      <b-table
        :items="members"
        id="table-members-whitelabel"
        :fields="$t('table_members_whitelabel')"
        head-variant="light"        
        responsive
        :busy="!loaded"
        show-empty
      >
        <template #cell(id)="data">
          <span>{{ data.item.id }}</span>
        </template>

        <template #cell(email)="data">
          <span>{{ data.item.email }}</span>
        </template>

        <template #cell(name)="data">
          <span>{{ data.item.first_name }} {{ data.item.last_name }}</span>
        </template>

        <template #cell(is_verified)="data">
          <b-badge :variant="data.item.is_verified ? 'light-success' : 'light-warning'">
            {{ data.item.is_verified ? $t('yes') : $t('no') }}
          </b-badge>
        </template>

        <template #cell(date_joined)="data">
          <span>{{ formatDate(data.item.date_joined) }}</span>
        </template>

        <template #cell(actions)="data">
          <b-button
            variant="flat-secondary"
            size="sm"
            @click="confirmDeleteMember(data.item)"
          >
            <feather-icon icon="TrashIcon" size="16" />
          </b-button>
        </template>

        <template #empty>
          <no-results
            v-if="members.length === 0 && loaded"
            :title="$t('profile.noData')"
            :show_button="false"
            :border="'border-0'"
          />
        </template>

        <template #table-busy>
          <div class="text-center my-2 h-min-empty d-flex align-items-center justify-content-center">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>
      </b-table>
      <pagination-brandme-v2 
        @update_current_page="updateCurrentPage" 
        :total_rows="total_rows" 
        :per_page="per_page" 
        :current_page="current_page" 
        v-if="loaded && members.length !== 0" 
        class="w-100 px-1 pb-1"
      />
    </div>
    
    <add-member-sidebar
      :is-open="is_sidebar_open"
      @close="closeSidebar"
      @member-added="handleMemberAdded"
    />
  </b-card>
</template>

<script>
import {
  BTable,
  BCard,
  BBadge,
  BFormInput,
  BSpinner,
  BButton,
} from 'bootstrap-vue';
import { getWhitelabelMembers, removeWhitelabelMember } from '@/services/settings';
import { format } from 'date-fns';
import { showToast } from '@/libs/utils/others';

let searchTimeout = null;

export default {
  components: {
    BTable,
    BCard,
    BBadge,
    BFormInput,
    BSpinner,
    BButton,
    paginationBrandmeV2: () => import('@/views/components/pagination/paginationBrandmeV2.vue'),
    noResults: () => import('@/views/pages/proposals/stepper/noResults.vue'),
    addMemberSidebar: () => import('./AddMemberSidebar.vue')
  },
  data() {
    return {
      members: [],
      total_rows: 0,
      per_page: 10,
      current_page: 1,
      loaded: false,
      sarch_query: '',
      is_sidebar_open: false,
    }
  },
  created() {
    this.fetchMembers();
  },
  computed: {

  },
  methods: {
    fetchMembers() {
      this.loaded = false;
      const payload = {};

      if (this.sarch_query) {
        payload.query = this.sarch_query;
      }

      getWhitelabelMembers(payload, {page: this.current_page, size_page: this.per_page })
        .then(response => {
          this.members = response.results || [];
          this.total_rows = response.count || 0;
          this.loaded = true;
        })
        .catch(() => {
          showToast(this, 'danger', 'XIcon', 'errorTitle', 'errorGettingData');
          this.members = [];
          this.total_rows = 0;
          this.loaded = true;
        });
    },
    handleSearch() {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        this.current_page = 1;
        this.fetchMembers();
      }, 500);
    },
    updateCurrentPage(page) {
      this.current_page = page;
      this.fetchMembers();
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      try {
        return format(new Date(dateString), 'dd/MM/yyyy');
      } catch (e) {
        return dateString;
      }
    },
    openAddMemberSidebar() {
      this.is_sidebar_open = true;
    },
    closeSidebar() {
      this.is_sidebar_open = false;
    },
    handleMemberAdded(newMember) {
      this.members.unshift(newMember);
      this.total_rows += 1;
      this.closeSidebar();
    },
    confirmDeleteMember(member) {
      this.$swal({
        title: this.$t('areYouSure'),
        text: `${this.$t('deleteMemberConfirm')} ${member.email}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('yesDelete'),
        cancelButtonText: this.$t('cancel'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.deleteMember(member);
        }
      });
    },
    deleteMember(member) {
      removeWhitelabelMember({ member_email: member.email })
        .then(() => {
          const index = this.members.findIndex(m => m.email === member.email);
          if (index !== -1) {
            this.members.splice(index, 1);
            this.total_rows -= 1;
          }
          showToast(this, 'success', 'CheckIcon', 'success', 'memberDeletedSuccessfully');
        })
        .catch(() => {
          showToast(this, 'danger', 'XIcon', 'errorTitle', 'errorDeletingMember');
        });
    }
  },
}
</script>

<style lang="scss" scoped>
.border-0 {
  border: 0 !important;
}
.main-container-current-membership {
  width: 100%;
  border: 1px solid #cccccc83;
  border-bottom-left-radius: 0.5em;
  border-bottom-right-radius: 0.5em;
  border-top: 0;
  display: flex;
  flex-direction: column;
  padding: 0;
}

.search-bar-container {
  .search-input {
    &::placeholder {
      font-family: 'avenir-medium';
    }
    max-width: 400px;
  }
}

.header-current {
  background-color: #fafafa;
  width: 100%;
  border: 1px solid #cccccc83;
  border-top-right-radius: 0.5em;
  border-top-left-radius: 0.5em;
  padding: 8px 16px;
  font-size: 16px;
  font-weight: 500;
}

.h-min-empty {
  min-height: 300px;
}

::v-deep #table-members-whitelabel {
  margin-bottom: 0;
  
  td {
    vertical-align: middle;
  }
}
</style>
